/* eslint-disable jsx-a11y/alt-text */
import { Box } from '@rebass/grid';
import { ImgHTMLAttributes, SourceHTMLAttributes, useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { UnreachableCaseError } from '../../entities/UnreachableCaseError';

type SourceProps = SourceHTMLAttributes<HTMLSourceElement>;
export interface BaseImgProps {
    /** Default is 1.2 */
    scaleAmount?: number;
    /**  Preferably some kind of low quality, low size image of the same kind as the src image */
    placeholder?: string;
    /**  In miliseconds. Default is 750 miliseconds */
    fadeInDuration?: number;
    /**  Amount of blur that is applied to the placeholder. Default is 20 pixels */
    blurAmount?: string;
    fallback: ImgHTMLAttributes<HTMLImageElement>;
    sources?: SourceProps[];
    loading?: 'eager' | 'lazy';
    className?: string;
    alt?: string;
    title?: string;
    width?: number | string;
    height?: number | string;
    addNoscript?: boolean;
}

export interface FillImgProps extends BaseImgProps {
    layout: 'fill';
}

export interface RatioImgProps extends BaseImgProps {
    ratio: number;
    layout: 'ratio';
}

export interface DefaultImgProps extends BaseImgProps {
    layout?: undefined;
    width: number | string;
    height: number | string;
}

export type ImgProps = FillImgProps | RatioImgProps | DefaultImgProps;

const imageCache: Record<string, boolean> = {};

const addToImageCache = (src: string) => {
    if (typeof document === 'undefined') {
        return;
    }

    imageCache[src] = true;
};

const isImageCached = (src: string | undefined): boolean => {
    if (typeof document === 'undefined') {
        return false;
    }
    if (!src) {
        return false;
    }
    return imageCache[src] || false;
};

const generateNoscriptSource = (props: SourceProps) => {
    const type = props.type ? `type=${props.type}` : '';
    const srcSet = props.srcSet ? `srcset=${props.srcSet}` : '';
    return `<source ${type}${srcSet}/>`;
};

const generateNoscriptSources = (...sources: SourceProps[]) => sources.map(generateNoscriptSource).join(``);

const noscriptImg = (props: ImgProps) => {
    // Check if prop exists before adding each attribute to the string output below to prevent
    // HTML validation issues caused by empty values like width="" and height=""
    const src = props.fallback.src ? `src="${props.fallback.src}" ` : `src="" `; // required attribute
    const srcSet = props.fallback.srcSet ? `srcset="${props.fallback.srcSet}" ` : ``;
    const title = props.title ? `title="${props.title}" ` : ``;
    const alt = props.alt ? `alt="${props.alt}" ` : `alt="" `; // required attribute
    const width = props.width ? `width="${props.width}" ` : ``;
    const height = props.height ? `height="${props.height}" ` : ``;
    const loading = props.loading ? `loading="${props.loading}" ` : ``;
    const sources = generateNoscriptSources(...(props.sources ?? []));
    return `<picture>${sources}<img ${loading}${width}${height}${srcSet}${src}${alt}${title}style="position:absolute;top:0;left:0;opacity:1;width:100%;height:100%;object-fit:cover;object-position:center"/></picture>`;
};

/**
 * There are basically three ways to use the `Img` component.
 *
 * 1. Default, where width and height are required and an image with static dimensions will be displayed
 * 2. `layout: fill` will fill its parent container
 * 3. `layout: ratio` will size the image according to the ratio that's given as a prop
 */
const Img = (props: ImgProps) => {
    const {
        placeholder,
        fadeInDuration = 500,
        blurAmount = '10px',
        scaleAmount = 1.2,
        fallback,
        sources,
        loading,
        width = '100%',
        height = '100%',
        alt,
        title,
        className,
        addNoscript = false,
        ...rest
    } = props;
    const showNoscript = addNoscript || loading === 'eager';
    const ref = useRef<HTMLImageElement>(null);
    const inCache = isImageCached(fallback.src);
    const [loaded, setLoaded] = useState(false);

    const onLoad = useCallback(() => {
        setLoaded(true);
        if (fallback.src) {
            addToImageCache(fallback.src);
        }
    }, [fallback.src]);

    useEffect(() => {
        // if the img has already been downloaded onLoad won't trigger so we do it manually
        if (ref.current?.complete) {
            onLoad();
        }
    }, [onLoad]);

    const srcRef = useRef(fallback.src);

    useEffect(() => {
        const resetImgState = () => {
            setLoaded(false);
        };

        if (srcRef.current !== fallback.src) {
            resetImgState();
        }

        srcRef.current = fallback.src;
    }, [fallback.src]);

    return (
        <Container
            key={fallback.src}
            width={width}
            height={height}
            className={className}
            ratio={rest.layout === 'ratio' ? rest.ratio : 0}
            layout={rest.layout}
        >
            <Placeholder
                title={title}
                alt={alt}
                width={width}
                height={height}
                blurAmount={blurAmount}
                scaleAmount={scaleAmount}
                src={placeholder}
                loading={loading}
                aria-hidden
                style={{
                    opacity: loaded ? 0 : 1,
                    transition: `opacity 0ms`,
                    transitionDelay: inCache ? '0ms' : `${fadeInDuration}ms`,
                }}
            />
            <Picture>
                {sources?.map((source, i) => (
                    <source key={i} {...source} />
                ))}
                <img
                    title={title}
                    alt={alt}
                    ref={ref}
                    width={width}
                    height={height}
                    loading={loading}
                    onLoad={onLoad}
                    {...fallback}
                    style={{
                        ...fallback.style,
                        opacity: loaded ? 1 : 0,
                        transition: 'opacity ease',
                        transitionDuration: inCache ? '0ms' : `${fadeInDuration}ms`,
                    }}
                />
                {/* Show the original image during server-side rendering */}
                {/*  eslint-disable-next-line react/no-danger */}
                {showNoscript && <noscript dangerouslySetInnerHTML={{ __html: noscriptImg(props) }} />}
            </Picture>
        </Container>
    );
};

export default Img;

const Placeholder = styled.img<{ blurAmount: string; scaleAmount: number }>`
    filter: blur(${({ blurAmount }) => blurAmount});
    transform: scale(${({ scaleAmount }) => scaleAmount});
    object-fit: cover;
    pointer-events: none;
    background: ${({ theme }) => theme.colors.neutral['10']};
`;

const parseDimension = (amount: number | string) => (typeof amount === 'number' ? `${amount}px` : amount);

interface ContainerProps {
    width: number | string;
    height: number | string;
    layout?: 'ratio' | 'fill';
    ratio?: number;
}

const Container = styled(Box)<ContainerProps>`
    width: ${({ width }) => parseDimension(width)};
    height: ${({ height }) => parseDimension(height)};
    overflow: hidden;

    ${({ layout, ratio = 0 }) => {
        if (!layout) {
            return undefined;
        }
        switch (layout) {
            case 'fill':
                return css`
                    position: absolute;
                    left: 0;
                    top: 0;
                    right: 0;
                    bottom: 0;
                `;
            case 'ratio':
                return css`
                    position: relative;
                    &:before {
                        display: block;
                        content: '';
                        width: 100%;
                        padding-top: ${Math.round(ratio * 100)}%;
                    }
                `;
            default:
                throw new UnreachableCaseError(layout);
        }
    }}

    img {
        position: absolute;
        left: 0;
        top: 0;
        right: 0;
        bottom: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
    }
`;

const Picture = styled.picture``;
